/*
 * Small dependency-free bash-syntax highlighter for the remote-shell UI.
 * Not a full parser — it handles the categories that make a shell line
 * scannable at a glance:
 *   - the leading command name
 *   - flags (--long, -x)
 *   - single- and double-quoted strings
 *   - variable references ($VAR, ${VAR})
 *   - operators / pipes (| & ; < > ( ))
 *   - comments (# to EOL)
 *   - keywords (if/then/else/fi/for/do/done/while/case/esac/in/function)
 *
 * The output is a run of <span class="hv-plant-shell__syn-*"> tokens with the raw
 * text HTML-escaped. Colours flow via CSS so light/dark themes work out of
 * the box.
 *
 * Extending: add a category to Kind, teach the loop to detect it, and add
 * the matching .hv-plant-shell__syn-<kind> rule in PlantShellPage.css.
 */

type Kind =
  | 'command'
  | 'flag'
  | 'string'
  | 'variable'
  | 'operator'
  | 'comment'
  | 'keyword'
  | 'number'
  | 'text';

const KEYWORDS = new Set([
  'if', 'then', 'else', 'elif', 'fi',
  'for', 'do', 'done', 'while', 'until',
  'case', 'esac', 'in',
  'function', 'return', 'break', 'continue',
  'export', 'local', 'declare', 'readonly', 'unset',
]);

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

export function highlightBash(source: string): string {
  const tokens: Array<{ kind: Kind; text: string }> = [];
  let i = 0;
  let atLineStart = true;

  while (i < source.length) {
    const ch = source[i];

    if (ch === '\n') {
      tokens.push({ kind: 'text', text: '\n' });
      i++;
      atLineStart = true;
      continue;
    }

    if (/\s/.test(ch)) {
      tokens.push({ kind: 'text', text: ch });
      i++;
      continue;
    }

    if (ch === '#') {
      const eol = source.indexOf('\n', i);
      const end = eol === -1 ? source.length : eol;
      tokens.push({ kind: 'comment', text: source.slice(i, end) });
      i = end;
      continue;
    }

    if (ch === "'") {
      const end = source.indexOf("'", i + 1);
      const stop = end === -1 ? source.length : end + 1;
      tokens.push({ kind: 'string', text: source.slice(i, stop) });
      i = stop;
      atLineStart = false;
      continue;
    }

    if (ch === '"') {
      // Double-quoted: consume until unescaped closing quote, but allow
      // escaped inner quotes. Simple state machine, not a full parser.
      let j = i + 1;
      while (j < source.length) {
        if (source[j] === '\\' && j + 1 < source.length) { j += 2; continue; }
        if (source[j] === '"') { j++; break; }
        j++;
      }
      tokens.push({ kind: 'string', text: source.slice(i, j) });
      i = j;
      atLineStart = false;
      continue;
    }

    if (ch === '$') {
      const rest = source.slice(i);
      const m = rest.match(/^\$(\{[^}]+\}|\w+|\$|!|\?)/);
      if (m) {
        tokens.push({ kind: 'variable', text: m[0] });
        i += m[0].length;
        atLineStart = false;
        continue;
      }
    }

    if (/[|&;<>()]/.test(ch)) {
      // Pipe / redirect / logical operator marks the start of a new command
      // context, so the next word can be highlighted as a command again.
      let end = i + 1;
      if (
        (ch === '|' && source[end] === '|') ||
        (ch === '&' && source[end] === '&') ||
        (ch === '<' && source[end] === '<') ||
        (ch === '>' && source[end] === '>')
      ) {
        end++;
      }
      tokens.push({ kind: 'operator', text: source.slice(i, end) });
      i = end;
      atLineStart = true;
      continue;
    }

    // Word — could be command, flag, keyword, number, or plain text.
    const wordMatch = source.slice(i).match(/^[^\s|&;<>()"'#$]+/);
    if (wordMatch) {
      const word = wordMatch[0];
      let kind: Kind;
      if (word.startsWith('-')) kind = 'flag';
      else if (KEYWORDS.has(word)) kind = 'keyword';
      else if (/^-?\d+(\.\d+)?$/.test(word)) kind = 'number';
      else if (atLineStart) kind = 'command';
      else kind = 'text';
      tokens.push({ kind, text: word });
      i += word.length;
      atLineStart = false;
      continue;
    }

    tokens.push({ kind: 'text', text: ch });
    i++;
    atLineStart = false;
  }

  return tokens
    .map((t) => t.kind === 'text'
      ? escapeHtml(t.text)
      : `<span class="hv-plant-shell__syn-${t.kind}">${escapeHtml(t.text)}</span>`)
    .join('');
}
